import type { Document as DocxDocument } from "docx";

type DocxModule = typeof import("docx");

type InlineStyle = {
    bold: boolean;
    italic: boolean;
    superscript: boolean;
    code: boolean;
};

type InlineChunk = {
    text?: string;
    break?: number;
    style: InlineStyle;
};

const FONT_FAMILY = "Arial";
const BODY_SIZE = 24;
const BODY_COLOR = "000000";
const INLINE_CONTAINER_TAGS = new Set([
    "a",
    "abbr",
    "b",
    "code",
    "em",
    "i",
    "mark",
    "small",
    "span",
    "strong",
    "sub",
    "sup",
    "u"
]);

const defaultInlineStyle = (): InlineStyle => ({
    bold: false,
    italic: false,
    superscript: false,
    code: false
});

function normalizeInlineText(text: string): string {
    return text.replace(/\s+/g, " ");
}

function makeTextRun(docx: DocxModule, text: string, style: InlineStyle, overrides: Record<string, unknown> = {}) {
    return new docx.TextRun({
        text,
        font: style.code ? "Courier New" : FONT_FAMILY,
        size: style.superscript ? 18 : BODY_SIZE,
        color: BODY_COLOR,
        bold: style.bold,
        italics: style.italic,
        superScript: style.superscript,
        ...overrides
    });
}

function chunksToTextRuns(docx: DocxModule, chunks: InlineChunk[], overrides: Record<string, unknown> = {}) {
    return chunks.map(chunk => {
        if (chunk.break) {
            return new docx.TextRun({ break: chunk.break, ...overrides });
        }
        return makeTextRun(docx, chunk.text ?? "", chunk.style, overrides);
    });
}

function inlineRunsFromNode(node: Node, inherited: InlineStyle): InlineChunk[] {
    if (node.nodeType === Node.TEXT_NODE) {
        const normalized = normalizeInlineText(node.textContent ?? "");
        return normalized.trim() ? [{ text: normalized, style: inherited }] : [];
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return [];
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (tag === "br") {
        return [{ break: 1, style: inherited }];
    }

    let style = { ...inherited };
    if (tag === "strong" || tag === "b") {
        style = { ...style, bold: true };
    }
    if (tag === "em" || tag === "i") {
        style = { ...style, italic: true };
    }
    if (tag === "sup" || (tag === "a" && element.classList.contains("supContainer"))) {
        style = { ...style, superscript: true };
    }
    if (tag === "code") {
        style = { ...style, code: true };
    }

    const children: InlineChunk[] = [];
    Array.from(element.childNodes).forEach(child => {
        children.push(...inlineRunsFromNode(child, style));
    });
    return children;
}

function inlineRunsFromNodes(nodes: NodeListOf<ChildNode> | ChildNode[]): InlineChunk[] {
    const runs: InlineChunk[] = [];
    Array.from(nodes).forEach(node => {
        runs.push(...inlineRunsFromNode(node, defaultInlineStyle()));
    });
    return runs;
}

function paragraphWithRuns(docx: DocxModule, runs: any[], options: Record<string, unknown> = {}) {
    return new docx.Paragraph({
        children: runs,
        spacing: { after: 160 },
        ...options
    });
}

function headingParagraph(docx: DocxModule, element: HTMLElement, level: number) {
    const sizes = [36, 32, 28, 26, 24, 24];
    const size = sizes[Math.max(0, Math.min(level - 1, sizes.length - 1))];
    const chunks = inlineRunsFromNodes(element.childNodes).map(chunk => ({
        ...chunk,
        style: { ...chunk.style, bold: true }
    }));
    const runs = chunksToTextRuns(docx, chunks, { size });

    return paragraphWithRuns(docx, runs.length > 0 ? runs : [makeTextRun(docx, element.innerText.trim(), { ...defaultInlineStyle(), bold: true }, { size })], {
        spacing: { before: 220, after: 180 }
    });
}

function extractListItemContent(li: HTMLElement): HTMLElement {
    const container = document.createElement("div");
    Array.from(li.childNodes).forEach(child => {
        if (child.nodeType === Node.ELEMENT_NODE) {
            const tag = (child as HTMLElement).tagName.toLowerCase();
            if (tag === "ul" || tag === "ol") {
                return;
            }
        }
        container.appendChild(child.cloneNode(true));
    });
    return container;
}

function renderList(docx: DocxModule, list: HTMLElement, output: any[], level = 0): void {
    const ordered = list.tagName.toLowerCase() === "ol";
    const items = Array.from(list.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName.toLowerCase() === "li");

    items.forEach((li, index) => {
        const prefix = ordered ? `${index + 1}. ` : "• ";
        const chunks = [
            { text: prefix, style: defaultInlineStyle() },
            ...inlineRunsFromNodes(extractListItemContent(li).childNodes)
        ];

        if (chunks.some(chunk => String(chunk.text ?? "").trim())) {
            output.push(
                paragraphWithRuns(docx, chunksToTextRuns(docx, chunks), {
                    spacing: { after: 100 },
                    indent: { left: 720 + level * 360, hanging: 240 }
                })
            );
        }

        Array.from(li.children)
            .filter((child): child is HTMLElement => child instanceof HTMLElement)
            .forEach(child => {
                const tag = child.tagName.toLowerCase();
                if (tag === "ul" || tag === "ol") {
                    renderList(docx, child, output, level + 1);
                }
            });
    });
}

function cellParagraphs(docx: DocxModule, cell: HTMLElement): any[] {
    const paragraphs: any[] = [];
    const blockChildren = Array.from(cell.children).filter((child): child is HTMLElement => child instanceof HTMLElement);

    if (blockChildren.length === 0) {
        const chunks = inlineRunsFromNodes(cell.childNodes);
        return [paragraphWithRuns(docx, chunks.length > 0 ? chunksToTextRuns(docx, chunks) : [new docx.TextRun("")], { spacing: { after: 60 } })];
    }

    blockChildren.forEach(child => {
        const tag = child.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag)) {
            paragraphs.push(headingParagraph(docx, child, Number.parseInt(tag[1], 10)));
            return;
        }
        if (tag === "p" || tag === "div") {
            const chunks = inlineRunsFromNodes(child.childNodes);
            if (chunks.length > 0) {
                paragraphs.push(paragraphWithRuns(docx, chunksToTextRuns(docx, chunks), { spacing: { after: 60 } }));
            }
            return;
        }
        if (tag === "ul" || tag === "ol") {
            renderList(docx, child, paragraphs, 0);
            return;
        }
        const chunks = inlineRunsFromNodes(child.childNodes);
        if (chunks.length > 0) {
            paragraphs.push(paragraphWithRuns(docx, chunksToTextRuns(docx, chunks), { spacing: { after: 60 } }));
        }
    });

    return paragraphs.length > 0 ? paragraphs : [new docx.Paragraph({ children: [] })];
}

function renderTable(docx: DocxModule, table: HTMLTableElement) {
    const rows = Array.from(table.rows);
    if (rows.length === 0) {
        return null;
    }

    const columnCount = Math.max(...rows.map(row => row.cells.length));
    const rowsOut = rows.map((row, rowIndex) => {
        const headerRow =
            rowIndex === 0 &&
            (row.parentElement?.tagName.toLowerCase() === "thead" || Array.from(row.cells).every(cell => cell.tagName.toLowerCase() === "th"));

        const cells = Array.from({ length: columnCount }, (_, colIndex) => {
            const cell = row.cells[colIndex] as HTMLElement | undefined;
            const children = cell ? cellParagraphs(docx, cell) : [new docx.Paragraph({ children: [] })];
            return new docx.TableCell({
                children,
                width: { size: Math.floor(100 / Math.max(columnCount, 1)), type: docx.WidthType.PERCENTAGE },
                shading: headerRow ? { fill: "F8F9FB" } : undefined,
                margins: { top: 80, bottom: 80, left: 100, right: 100 }
            });
        });

        return new docx.TableRow({
            children: cells,
            tableHeader: headerRow
        });
    });

    return new docx.Table({
        rows: rowsOut,
        width: { size: 100, type: docx.WidthType.PERCENTAGE },
        borders: {
            top: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" },
            bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" },
            left: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" },
            right: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" },
            insideHorizontal: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" },
            insideVertical: { style: docx.BorderStyle.SINGLE, size: 1, color: "777777" }
        }
    });
}

function shouldFlattenAsInline(element: HTMLElement): boolean {
    const tag = element.tagName.toLowerCase();
    if (INLINE_CONTAINER_TAGS.has(tag)) {
        return true;
    }

    return Array.from(element.children).every(child => INLINE_CONTAINER_TAGS.has(child.tagName.toLowerCase()));
}

function walkBlockChildren(docx: DocxModule, element: HTMLElement, output: any[]): void {
    Array.from(element.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const normalized = normalizeInlineText(node.textContent ?? "");
            if (normalized.trim()) {
                output.push(paragraphWithRuns(docx, [makeTextRun(docx, normalized.trim(), defaultInlineStyle())]));
            }
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const child = node as HTMLElement;
        const tag = child.tagName.toLowerCase();

        if (/^h[1-6]$/.test(tag)) {
            output.push(headingParagraph(docx, child, Number.parseInt(tag[1], 10)));
            return;
        }

        if (tag === "p") {
            const chunks = inlineRunsFromNodes(child.childNodes);
            if (chunks.length > 0) {
                output.push(paragraphWithRuns(docx, chunksToTextRuns(docx, chunks), { spacing: { after: 160 } }));
            }
            return;
        }

        if (tag === "ul" || tag === "ol") {
            renderList(docx, child, output, 0);
            output.push(new docx.Paragraph({ children: [], spacing: { after: 80 } }));
            return;
        }

        if (tag === "blockquote") {
            const chunks = inlineRunsFromNodes(child.childNodes).map(chunk => ({
                ...chunk,
                style: { ...chunk.style, italic: true }
            }));
            if (chunks.length > 0) {
                output.push(
                    paragraphWithRuns(docx, chunksToTextRuns(docx, chunks), {
                        indent: { left: 720 },
                        border: {
                            left: {
                                style: docx.BorderStyle.SINGLE,
                                size: 6,
                                color: "D1DBFA"
                            }
                        },
                        spacing: { after: 160 }
                    })
                );
            }
            return;
        }

        if (tag === "pre") {
            output.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: child.innerText.trimEnd(),
                            font: "Courier New",
                            size: 22,
                            color: BODY_COLOR
                        })
                    ],
                    spacing: { after: 160 }
                })
            );
            return;
        }

        if (tag === "hr") {
            output.push(
                new docx.Paragraph({
                    children: [],
                    spacing: { before: 100, after: 160 },
                    border: {
                        bottom: {
                            style: docx.BorderStyle.SINGLE,
                            size: 1,
                            color: "B4B4B4"
                        }
                    }
                })
            );
            return;
        }

        if (tag === "table" && child instanceof HTMLTableElement) {
            const renderedTable = renderTable(docx, child);
            if (renderedTable) {
                output.push(new docx.Paragraph({ children: [], spacing: { after: 80 } }));
                output.push(renderedTable);
                output.push(new docx.Paragraph({ children: [], spacing: { after: 120 } }));
            }
            return;
        }

        if (tag === "div") {
            walkBlockChildren(docx, child, output);
            return;
        }

        if (!shouldFlattenAsInline(child)) {
            walkBlockChildren(docx, child, output);
            return;
        }

        const chunks = inlineRunsFromNodes(child.childNodes);
        if (chunks.length > 0) {
            output.push(paragraphWithRuns(docx, chunksToTextRuns(docx, chunks)));
        }
    });
}

export async function exportRenderedAnswerToDocx(
    renderedAnswerElement: HTMLElement,
    options: { citations: string[]; citationSectionTitle: string; fileName: string }
): Promise<void> {
    const docx = await import("docx");
    const { saveAs } = await import("file-saver");

    const container = renderedAnswerElement.cloneNode(true) as HTMLElement;
    const children: any[] = [];
    walkBlockChildren(docx, container, children);

    if (options.citations.length > 0) {
        children.push(new docx.Paragraph({ children: [], spacing: { before: 220, after: 80 } }));
        children.push(
            paragraphWithRuns(
                docx,
                [makeTextRun(docx, options.citationSectionTitle, { ...defaultInlineStyle(), bold: true })],
                { spacing: { after: 120 } }
            )
        );
        options.citations.forEach((citation, index) => {
            children.push(
                paragraphWithRuns(docx, [makeTextRun(docx, `${index + 1}. ${citation}`, defaultInlineStyle(), { size: 22 })], {
                    spacing: { after: 100 }
                })
            );
        });
    }

    const fallbackText = container.textContent?.trim() || "No content available";
    const doc = new docx.Document({
        sections: [
            {
                properties: {},
                children:
                    children.length > 0
                        ? children
                        : [paragraphWithRuns(docx, [makeTextRun(docx, fallbackText, defaultInlineStyle())], { spacing: { after: 160 } })]
            }
        ]
    }) as DocxDocument;

    const blob = await docx.Packer.toBlob(doc);
    saveAs(blob, options.fileName);
}
